// Pure, testable auto-scheduler. No DB calls in here — the caller passes
// inputs in and applies the returned assignments.
//
// Contract:
//   autoSchedule({
//     weekStart,           // YYYY-MM-DD Monday
//     days,                // array of 6 YYYY-MM-DD (Mon..Sat)
//     jobs,                // [{ id, business_id, start_date, end_date, status, crew_leader_id, crew_size_needed }]
//     workers,             // [{ id, role, seniority_rank, max_days_per_week, preferred_days_off,
//                          //    season_start, season_end, availability_status }]
//     approvedDayOffs,     // [{ user_id, request_date }]
//     locked,              // Map<`${user_id}|${date}`, scheduleRow>  — existing rows to preserve
//   })
//
// Returns:
//   [{ user_id, schedule_date, status, job_id|null, assigned_by:null, notes:null }]
//
// `status` is one of: 'scheduled' | 'day_off'
// Locked rows are passed back untouched.

export const CREW_SIZE_DEFAULT = { pools: 5, riverbend: 3 }
export const ALWAYS_SCHEDULED_ROLES = ['owner', 'project_manager', 'crew_leader']

function iso(d) { return d.toISOString().slice(0, 10) }
export function mondayOf(date) {
  const d = new Date(date)
  const day = d.getDay() // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day // back to Monday
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}
export function weekDays(monday) {
  const out = []
  for (let i = 0; i < 6; i++) {
    const d = new Date(monday)
    d.setDate(d.getDate() + i)
    out.push(iso(d))
  }
  return out
}

function workerInSeason(w, day) {
  if (w.availability_status === 'inactive') return false
  if (w.availability_status === 'off_season') return false
  if (w.season_start && day < w.season_start) return false
  if (w.season_end && day > w.season_end) return false
  return true
}
function jobActiveOn(job, day) {
  if (!['scheduled', 'active', 'in_progress'].includes(job.status)) return false
  if (job.start_date && day < job.start_date) return false
  if (job.end_date && day > job.end_date) return false
  return true
}
function crewSize(job) {
  if (job.crew_size_needed && job.crew_size_needed > 0) return job.crew_size_needed
  return CREW_SIZE_DEFAULT[job.business_id] || 3
}
function dayOfWeekLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return ['sun','mon','tue','wed','thu','fri','sat'][d.getDay()]
}

export function autoSchedule({ weekStart, days, jobs, workers, approvedDayOffs = [], blockedDates = [], workerAvailability = [], locked }) {
  const lockedMap = locked instanceof Map ? locked : new Map()
  const dayOffSet = new Set(
    (approvedDayOffs || []).map((r) => r.user_id + '|' + r.request_date)
  )
  // Blocked dates set (approved only)
  const blockedSet = new Set(
    (blockedDates || []).filter(function(b) { return b.status === 'approved' }).map(function(b) { return b.user_id + '|' + b.blocked_date })
  )
  // Availability map: user_id|dow -> { available, available_after }
  const availMap = {}
  ;(workerAvailability || []).forEach(function(a) { availMap[a.user_id + '|' + a.day_of_week] = a })

  // Running count of days assigned per worker this week (seeded from locked rows)
  const workedCount = {}
  workers.forEach((w) => { workedCount[w.id] = 0 })
  for (const [, row] of lockedMap) {
    if (row && row.status === 'scheduled' && workedCount[row.user_id] !== undefined) {
      workedCount[row.user_id] += 1
    }
  }

  const assignments = []

  // Emit any locked rows first (they take precedence)
  for (const [, row] of lockedMap) assignments.push(row)

  for (const day of days) {
    // In-season, not on approved day-off, not locked for today, under max
    var dayDow = new Date(day + 'T00:00:00').getDay() // eslint-disable-line no-loop-func
    const candidates = workers.filter((w) => { // eslint-disable-line no-loop-func
      if (!workerInSeason(w, day)) return false
      if (dayOffSet.has(w.id + '|' + day)) return false
      if (blockedSet.has(w.id + '|' + day)) return false
      if (lockedMap.has(w.id + '|' + day)) return false
      // Check weekly availability
      var avKey = w.id + '|' + dayDow
      if (availMap[avKey] && !availMap[avKey].available) return false
      const max = w.max_days_per_week || 6
      if ((workedCount[w.id] || 0) >= max) return false
      return true
    })

    // Jobs active this day (for this business in the pool's business list)
    const todaysJobs = jobs.filter((j) => jobActiveOn(j, day))

    // Required headcount across jobs
    const needed = todaysJobs.reduce((s, j) => s + crewSize(j), 0)

    // Split candidates: managers/leaders always scheduled; laborers sorted by seniority
    const managers = candidates.filter((w) => ALWAYS_SCHEDULED_ROLES.includes(w.role))
    const laborers = candidates
      .filter((w) => !ALWAYS_SCHEDULED_ROLES.includes(w.role))
      .sort((a, b) => {
        const aPrefersOff = (a.preferred_days_off || []).includes(dayOfWeekLabel(day))
        const bPrefersOff = (b.preferred_days_off || []).includes(dayOfWeekLabel(day))
        if (aPrefersOff !== bPrefersOff) return aPrefersOff ? 1 : -1 // if prefers off, push down
        const ra = a.seniority_rank || 999
        const rb = b.seniority_rank || 999
        if (ra !== rb) return ra - rb
        return (a.id || '').localeCompare(b.id || '')
      })

    // Fill crews per job: crew_leader first if available, then seniority-sorted laborers
    const assignedToday = new Set()
    const managerPool = [...managers]
    const laborerPool = [...laborers]

    for (const job of todaysJobs) {
      const size = crewSize(job)
      let filled = 0

      // Seat the job's own crew leader first if they're available
      if (job.crew_leader_id) {
        const idx = managerPool.findIndex((m) => m.id === job.crew_leader_id)
        if (idx >= 0) {
          const leader = managerPool.splice(idx, 1)[0]
          assignments.push({ user_id: leader.id, schedule_date: day, status: 'scheduled', job_id: job.id, assigned_by: null, notes: null })
          assignedToday.add(leader.id)
          workedCount[leader.id] = (workedCount[leader.id] || 0) + 1
          filled += 1
        }
      }
      // Fill remaining slots: managers first (any remaining), then laborers by seniority
      while (filled < size) {
        let picked = null
        if (managerPool.length > 0) picked = managerPool.shift()
        else if (laborerPool.length > 0) picked = laborerPool.shift()
        else break
        assignments.push({ user_id: picked.id, schedule_date: day, status: 'scheduled', job_id: job.id, assigned_by: null, notes: null })
        assignedToday.add(picked.id)
        workedCount[picked.id] = (workedCount[picked.id] || 0) + 1
        filled += 1
      }
    }

    // Any remaining managers (not slotted to a job) still get scheduled — floating
    for (const m of managerPool) {
      assignments.push({ user_id: m.id, schedule_date: day, status: 'scheduled', job_id: null, assigned_by: null, notes: null })
      assignedToday.add(m.id)
      workedCount[m.id] = (workedCount[m.id] || 0) + 1
    }

    // Workers not assigned today → day_off
    for (const w of candidates) {
      if (!assignedToday.has(w.id)) {
        assignments.push({ user_id: w.id, schedule_date: day, status: 'day_off', job_id: null, assigned_by: null, notes: null })
      }
    }

    // (Suppress unused-var lint on needed — keep for debugging)
    void needed
  }

  return assignments
}

// Convenience: summarize assignments for a given week into per-day stats
export function summarizeWeek(assignments, days) {
  const byDay = {}
  days.forEach((d) => { byDay[d] = { scheduled: 0, off: 0 } })
  assignments.forEach((a) => {
    if (!byDay[a.schedule_date]) byDay[a.schedule_date] = { scheduled: 0, off: 0 }
    if (a.status === 'scheduled') byDay[a.schedule_date].scheduled += 1
    else if (a.status === 'day_off') byDay[a.schedule_date].off += 1
  })
  return byDay
}
